import { BrandMark } from "@/components/BrandMark";

/** Shared sign-in / forgot / reset / activate frame in the authenticated
 *  product language.
 *
 *  `eyebrow`, `heading`, and `lede` are overridable because they were not, and
 *  every page using this frame announced "Sign in to continue" — including the
 *  apply form, the password-reset pages, and account activation, none of which
 *  are signing in. The most prominent line on the page was telling people to do
 *  something other than what the page does. Defaults are the sign-in wording, so
 *  the page this frame was written for is unchanged.
 *
 *  The card keeps the scholarly face and the auth tokens (ADR 0029 §4); the
 *  form inside it follows the kit's field grammar. */
interface AuthCardProps {
  brandName: string;
  logoUrl: string | null;
  subtitle?: string;
  eyebrow?: string;
  heading?: string;
  lede?: string;
  children: React.ReactNode;
}

export function AuthCard({
  brandName,
  logoUrl,
  subtitle,
  eyebrow = "Member access",
  heading = "Sign in to continue",
  lede = "Use your academy account to enter the portal.",
  children,
}: AuthCardProps) {
  const sub = subtitle ?? `The ${brandName} portal`;
  return (
    <main
      data-auth-page
      data-auth-surface="default"
      className="grid min-h-screen place-items-center bg-background px-5 py-8 sm:px-8"
    >
      <div
        data-auth-card
        className="grid w-full max-w-[900px] overflow-hidden rounded-lg border border-border bg-card shadow-[var(--elevation-overlay)] md:grid-cols-[0.9fr_1.1fr]"
      >
        <section
          data-auth-brand-panel
          className="flex min-h-[250px] flex-col justify-between bg-primary px-8 py-9 text-primary-foreground sm:px-10 md:min-h-[560px] md:py-11"
        >
          <div>
            <BrandMark
              name={brandName}
              logoUrl={logoUrl}
              className="size-16 brightness-0 invert"
            />
            <h1 className="mt-6 max-w-[9ch] font-display text-[38px] leading-[0.98] tracking-[-0.02em]">
              {brandName}
            </h1>
            <p className="mt-3 text-ui text-on-primary-muted">{sub}</p>
          </div>

          <div className="mt-8 border-t border-on-primary-muted pt-6">
            <p className="max-w-[28ch] text-meta text-on-primary-muted">
              Your courses, records, and institutional work in one place.
            </p>
          </div>
        </section>

        {/* `min-w-0` because a grid item defaults to a min-content floor, and
            the widest thing these panels hold is an email address — one
            unbreakable token. Without it the panel grows past the card, the
            card clips its own overflow, and on a phone the primary button is
            sliced off the right edge. */}
        <section
          data-auth-form-panel
          className="flex min-w-0 items-center px-8 py-10 sm:px-12 md:px-14"
        >
          <div className="w-full">
            <p className="eyebrow">{eyebrow}</p>
            <h2 className="mt-2 font-display text-[34px] leading-none text-foreground">{heading}</h2>
            <p className="mt-3 text-ui text-muted-foreground">{lede}</p>
            <div className="mt-8 text-left">{children}</div>
          </div>
        </section>
      </div>
    </main>
  );
}
